import { useState, useEffect, useMemo } from 'react';
import MainFooter from "../../Components/Footer/MainFooter";
import MainHeader from "../../Components/Header/MainHeader";
import ContactGate from "../../Components/ContactGate";
import ReviewSection from "../../Components/ReviewSection";
import { MapPin, Home, Building, Bed, Filter, Search, Heart, Share2, Shield, Camera, ArrowLeft, Map as MapIcon, List as ListIcon, X } from 'lucide-react';
import { fetchAgencies, filterAgencies, LISTING_TYPES } from '../../Database/RentalManager/managedProperties';
import PropertiesMap from '../../Components/PropertiesMap';
import countyData from '../../data/county.json';
import banner from "../../assets/r-propertybanner.png"

const FindRental = () => {
  // "propertyManagers" holds agencies fetched from the live R-Property project — each
  // one already carries its own nested `.properties` list (see fetchAgencies), so there
  // is no separate per-manager fetch once one is selected.
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    county: '',
    constituency: '',
    businessname: ''
  });
  const [constituencies, setConstituencies] = useState([]);
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [selectedMapProperty, setSelectedMapProperty] = useState(null);

  // Fetch agencies (property managers + their listings) from the live R-Property project
  useEffect(() => {
    const load = async () => {
      try {
        const agencies = await fetchAgencies();
        setPropertyManagers(agencies);
        setFilteredManagers(agencies);
      } catch (error) {
        console.error("Error fetching property managers:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Update constituencies when county changes
  useEffect(() => {
    if (filters.county) {
      const selectedCounty = countyData.County.find(
        county => county.county_name === filters.county
      );
      if (selectedCounty) {
        setConstituencies(selectedCounty.constituencies);
        setFilters(prev => ({ ...prev, constituency: '' }));
      }
    } else {
      setConstituencies([]);
    }
  }, [filters.county]);

  // Apply filters and search to property managers
  useEffect(() => {
    let filtered = filterAgencies(propertyManagers, { search: searchTerm });

    if (filters.county) {
      filtered = filtered.filter(manager => manager.county === filters.county);
    }

    if (filters.constituency) {
      filtered = filtered.filter(manager => manager.constituency === filters.constituency);
    }

    if (filters.businessname) {
      filtered = filtered.filter(manager =>
        manager.name?.toLowerCase().includes(filters.businessname.toLowerCase())
      );
    }

    setFilteredManagers(filtered);
  }, [filters, propertyManagers, searchTerm]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleFavorite = (id) => {
    setFavorites(prev =>
      prev.includes(id)
        ? prev.filter(favId => favId !== id)
        : [...prev, id]
    );
  };

  const handleViewManager = (manager) => {
    setSelectedManager(manager);
  };

  const handleBackToManagers = () => {
    setSelectedManager(null);
  };

  const formatPrice = (price, type) => {
    if (!price) return 'Price on request';
    const numPrice = parseFloat(price);
    if (numPrice >= 1000000) {
      return `KSh ${(numPrice / 1000000).toFixed(1)}M ${type === LISTING_TYPES.RENT ? '/month' : ''}`;
    } else if (numPrice >= 1000) {
      return `KSh ${(numPrice / 1000).toFixed(0)}K ${type === LISTING_TYPES.RENT ? '/month' : ''}`;
    }
    return `KSh ${numPrice.toLocaleString()} ${type === LISTING_TYPES.RENT ? '/month' : ''}`;
  };

  const managerProperties = useMemo(() => selectedManager?.properties ?? [], [selectedManager]);

  // Every listing across every currently-filtered agency, flattened for the map — each
  // property already carries its own `.manager`, from the same fetchAgencies() pass.
  const allProperties = useMemo(
    () => filteredManagers.flatMap((manager) => manager.properties ?? []),
    [filteredManagers]
  );

  const PropertyManagerCard = ({ manager }) => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Header */}
      <div className="relative h-32 bg-navy">
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={() => toggleFavorite(manager.id)}
            className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
          >
            <Heart className={`w-4 h-4 ${favorites.includes(manager.id) ? 'text-red-500 fill-current' : 'text-gray-600'}`} />
          </button>
          <button className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors">
            <Share2 className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Manager Avatar */}
        <div className="absolute -bottom-8 left-6">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
            <Building className="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Content — contact info is deliberately not shown at list level, same as the
          app's agency directory. Reaching a manager goes through the gated contact
          button once you open their listings below. */}
      <div className="p-6 pt-12">
        <div className="mb-4">
          <h3 className="font-bold text-lg text-gray-900 mb-1">{manager.name}</h3>
          <p className="text-gray-600 text-sm">Managed by {manager.contactName}</p>
        </div>

        <div className="space-y-2 text-sm text-gray-600 mb-4">
          {manager.area && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              <span>{manager.area}</span>
            </div>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{manager.rentCount} to let</span>
            <span>{manager.saleCount} for sale</span>
          </div>
        </div>

        <button
          onClick={() => handleViewManager(manager)}
          className="w-full bg-navy text-white py-2 px-4 rounded-lg hover:bg-navy-dark transition-colors text-sm font-medium"
        >
          View Properties
        </button>
      </div>
    </div>
  );

  const PropertyCard = ({ property }) => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      {/* Image Carousel */}
      <div className="relative h-48 bg-gray-200 overflow-hidden">
        {property.cover ? (
          <img
            src={property.cover.url}
            alt={property.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-300 flex items-center justify-center">
            <Home className="w-16 h-16 text-gray-500" />
          </div>
        )}

        {/* Overlay buttons */}
        <div className="absolute top-3 right-3 flex gap-2">
          <button
            onClick={() => toggleFavorite(property.id)}
            className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
          >
            <Heart className={`w-4 h-4 ${favorites.includes(property.id) ? 'text-red-500 fill-current' : 'text-gray-600'}`} />
          </button>
          <button className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors">
            <Share2 className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Property type badge */}
        <div className="absolute top-3 left-3">
          <span className="px-3 py-1 bg-black/80 text-white text-xs rounded-full">
            {property.listingType}
          </span>
        </div>

        {/* Media count */}
        {property.media.length > 1 && (
          <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-full text-xs">
            <Camera className="w-3 h-3" />
            <span>{property.media.length}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="flex justify-between items-start mb-3">
          <h3 className="font-bold text-lg text-gray-900 line-clamp-2">{property.name}</h3>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">
              {formatPrice(property.rentAmount, property.listingType)}
            </p>
          </div>
        </div>

        <div className="space-y-2 text-sm text-gray-600 mb-4">
          {property.address && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-red-500" />
              <span>{property.address}</span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Building className="w-4 h-4 text-blue-500" />
            <span className="capitalize">{property.type}</span>
          </div>

          {property.units !== null && (
            <div className="flex items-center gap-2">
              <Bed className="w-4 h-4 text-purple-500" />
              <span>{property.units} unit{property.units > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {property.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {property.description}
          </p>
        )}

        {/* Contact — gated the same way as the manager's own contact card, since a
            listing's number is the manager's number. Tracked per-property (not per
            manager) so the R-Property dashboard can show view/contact counts per
            listing. */}
        <ContactGate
          subjectType="property"
          subjectId={property.id}
          name={property.manager.name}
          phone={property.manager.phone}
          email={property.manager.email}
          size="compact"
        />

        {/* Only listings with a pin (dropped through the dashboard's LocationPicker)
            can offer directions — older ones just don't have one. */}
        {property.location && (
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${property.location.latitude},${property.location.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <MapPin className="w-4 h-4" />
            Get Directions
          </a>
        )}
      </div>
    </div>
  );

  return (
    <div className='flex flex-col min-h-screen bg-gray-50'>
      <MainHeader />

      {/* Hero Banner Section */}
      <div
        className="relative bg-cover bg-center text-white py-20"
        style={{ backgroundImage: `url(${banner})` }}
      >
        <div className="absolute inset-0 bg-navy bg-opacity-70"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Find Your Dream Property</h1>
            <p className="text-xl mb-8 opacity-90">Browse verified properties across Kenya and connect directly with the agent or homeowner, no middlemen involved</p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by business name, owner, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-white/20"
                />
              </div>
            </div>

            {/* Download Section */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold mb-4">For Property Seekers</h3>
                <p className="mb-4 opacity-90">Find and book your perfect property on the go</p>
                <a
                  href="https://play.google.com/store/apps/details?id=com.rekebisha.RekebishaClient&pcampaignid=web_share"
                  className="inline-flex items-center bg-navy text-white px-6 py-3 rounded-lg hover:bg-navy-dark transition-colors"
                >
                  <img src="https://images.unsplash.com/photo-1606836576983-8b458e75221d?w=24&h=24" alt="Play Store" className="w-6 h-6 mr-2" />
                  Download Client App
                </a>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h3 className="text-2xl font-bold mb-4">For Property Owners</h3>
                <p className="mb-4 opacity-90">Manage and showcase your properties professionally</p>
                <a
                  href="https://property.rekebisha.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Building className="w-5 h-5 mr-2" />
                  Login as Property Manager
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-navy text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <h3 className="text-3xl font-bold mb-2">{propertyManagers.length}+</h3>
              <p className="text-gray-300">Property Managers</p>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-2">47</h3>
              <p className="text-gray-300">Counties Covered</p>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-2">1000+</h3>
              <p className="text-gray-300">Happy Clients</p>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-2">24/7</h3>
              <p className="text-gray-300">Support Available</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Advanced Filters Sidebar */}
          <div className="w-full lg:w-1/4">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <div className="flex items-center gap-2 mb-6">
                <Filter className="w-5 h-5" />
                <h2 className="text-xl font-semibold">Filter Managers</h2>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">County</label>
                  <select
                    name="county"
                    value={filters.county}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy"
                  >
                    <option value="">All Counties</option>
                    {countyData.County.map((county) => (
                      <option key={county.county_name} value={county.county_name}>
                        {county.county_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Constituency</label>
                  <select
                    name="constituency"
                    value={filters.constituency}
                    onChange={handleFilterChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy"
                    disabled={!filters.county}
                  >
                    <option value="">All Constituencies</option>
                    {constituencies.map((constituency) => (
                      <option key={constituency.constituency_name} value={constituency.constituency_name}>
                        {constituency.constituency_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                  <input
                    type="text"
                    name="businessname"
                    value={filters.businessname}
                    onChange={handleFilterChange}
                    placeholder="Search business name..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy"
                  />
                </div>

                <button
                  onClick={() => {
                    setFilters({
                      county: '',
                      constituency: '',
                      businessname: ''
                    });
                    setSearchTerm('');
                  }}
                  className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="w-full lg:w-3/4">
            {!selectedManager ? (
              <>
                {/* Property Managers Header */}
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {filteredManagers.length} Property Managers Found
                    </h3>
                    <p className="text-gray-600">
                      {searchTerm && `Results for "${searchTerm}"`}
                    </p>
                  </div>

                  <div className="flex bg-white rounded-lg shadow-sm border overflow-hidden shrink-0">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                        viewMode === 'list' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <ListIcon size={16} />
                      List
                    </button>
                    <button
                      onClick={() => {
                        setSelectedMapProperty(null);
                        setViewMode('map');
                      }}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                        viewMode === 'map' ? 'bg-navy text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <MapIcon size={16} />
                      Map
                    </button>
                  </div>
                </div>

                {/* Property Managers Grid, or every listing on one map */}
                {loading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
                  </div>
                ) : viewMode === 'map' ? (
                  <div className="relative">
                    <PropertiesMap
                      properties={allProperties}
                      selectedId={selectedMapProperty?.id}
                      onSelectProperty={setSelectedMapProperty}
                    />

                    {selectedMapProperty && (
                      <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:w-96 bg-white rounded-xl shadow-2xl p-4">
                        <button
                          onClick={() => setSelectedMapProperty(null)}
                          className="absolute top-2 right-2 p-1.5 hover:bg-gray-100 rounded-full"
                          aria-label="Close"
                        >
                          <X size={16} />
                        </button>
                        <h4 className="font-bold text-gray-900 pr-6">{selectedMapProperty.name}</h4>
                        <p className="text-sm text-gray-500 mb-1">{selectedMapProperty.address}</p>
                        <p className="text-sm text-gray-600 mb-3">
                          Listed by {selectedMapProperty.manager.name}
                        </p>
                        <div className="space-y-2">
                          <button
                            onClick={() => handleViewManager(selectedMapProperty.manager)}
                            className="w-full bg-navy text-white py-2 rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors"
                          >
                            View this listing
                          </button>
                          {selectedMapProperty.location && (
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${selectedMapProperty.location.latitude},${selectedMapProperty.location.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 w-full py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <MapPin className="w-4 h-4" />
                              Get Directions
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : filteredManagers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredManagers.map((manager) => (
                      <PropertyManagerCard key={manager.id} manager={manager} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white rounded-xl">
                    <Building className="w-16 h-16 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Property Managers Found</h3>
                    <p>Try adjusting your filters or search terms</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Manager Properties View */}
                <div className="mb-6">
                  <button
                    onClick={handleBackToManagers}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Property Managers
                  </button>

                  <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center flex-shrink-0">
                        <Building className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedManager.name}</h2>
                        <p className="text-gray-600 mb-2">Managed by {selectedManager.contactName}</p>
                        {selectedManager.area && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{selectedManager.area}</span>
                          </div>
                        )}
                      </div>
                      <div className="w-full sm:w-64">
                        <ContactGate
                          subjectType="agency"
                          subjectId={selectedManager.id}
                          name={selectedManager.name}
                          phone={selectedManager.phone}
                          email={selectedManager.email}
                        />
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Properties ({managerProperties.length})
                  </h3>
                </div>

                {/* Properties Grid */}
                {managerProperties.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {managerProperties.map((property) => (
                      <PropertyCard key={property.id} property={property} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white rounded-xl">
                    <Home className="w-16 h-16 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No Properties Found</h3>
                    <p>This manager hasn't uploaded any properties yet</p>
                  </div>
                )}

                <div className="mt-8">
                  <ReviewSection
                    subjectType="agency"
                    subjectId={selectedManager.id}
                    subjectName={selectedManager.name}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Featured Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Why Choose Our Platform?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We connect you with verified property managers across Kenya</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Verified Managers</h3>
              <p className="text-gray-600">All property managers are verified for authenticity and reliability</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Easy Discovery</h3>
              <p className="text-gray-600">Find property managers by location and business type</p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Protected Contact</h3>
              <p className="text-gray-600">Sign in with Google to reach a manager directly, no scraped numbers</p>
            </div>
          </div>
        </div>
      </div>

      <MainFooter />
    </div>
  );
};

export default FindRental;
